import {
  matchPath,
  useParams,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { useToggle } from 'usehooks-ts';
import cloneDeep from 'lodash.clonedeep';
import { useContext, useEffect, useState } from 'react';

import Toolbar from '../toolbar';
import {
  GetAreaById,
  DeleteAreaAsync,
  UpdateAreaAsync,
  UpdateGameDocState,
  UpdateResourceAsync,
  GetResourceValue
} from '../../../utils/game-document';
import { Algorithm } from '../../../types/algorithm';
import { FluidForm } from '../../../components/forms';
import RequiredFields from '../../../types/required-fields';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { AreaEditor } from '../../../features/game-document/areas';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../contexts/game-document';
import { AreaEvent } from '../../../types/game-document/entities/area';
import { AreaEntity, ResourceEntity } from '../../../types/game-document/';
import { ActionTopRow } from '../../../components/game-designer/action-top-row';

const Area = () => {
  const { areaId } = useParams();
  const [state, setState] = useContext(GameDocumentContext);
  const navigate = useNavigate();
  const [requiredFields, setRequiredFields] = useState<
    RequiredFields<AreaEntity>[]
  >([{ name: 'name', errorMessage: '' }]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useToggle(false);

  const getEntityById = (entityId: string) =>
    state.gameDocument?.assets.areas?.find((i) => i.id === entityId)!;

  const [entity, setEntity] = useState<AreaEntity>(() => {
    const areaEntity: AreaEntity = getEntityById(areaId!);
    return cloneDeep(areaEntity);
  });
  const [resources, setResources] = useState<ResourceEntity[]>([]);

  useEffect(() => {
    if (entity) {
      // Set the page title.
      document.title = `${GetResourceValue(state.gameDocument!, entity.titleResId ?? '')} - ${state.gameDocument?.name}`;
      const areaRes = state.gameDocument?.resources.find(
        (item) => item.id === entity.titleResId!
      )! as ResourceEntity;
      if (areaRes !== undefined) {
        setResources([areaRes]);
      }
    }
  }, [state, entity]);

  const handleAreaChange = (
    key: string,
    value: string | Algorithm,
    type: 'entity' | 'algorithm'
  ) => {
    if (type === 'entity') {
      setRequiredFields((prev) =>
        prev.map((input: RequiredFields<AreaEntity>) => {
          const copyInput: RequiredFields<AreaEntity> = cloneDeep({ ...input });
          const entityKey = key as keyof AreaEntity;
          copyInput.errorMessage = '';
          if (entityKey === 'name') {
            const duplicateValueIndex: number =
              state.gameDocument?.assets.areas
                ?.filter((area: AreaEntity) => area.id !== entity.id)
                .findIndex((area: AreaEntity) => area.name === value) ?? -1;
            if (value === '') {
              copyInput.errorMessage = 'This field is required';
            } else if (duplicateValueIndex !== -1) {
              copyInput.errorMessage = 'Please enter unique name';
            }
          }
          return copyInput;
        })
      );
      if (key === 'titleResId') {
        setResources(
          resources.map((item) =>
            item.id === entity[key]
              ? { ...item, value: value! as string }
              : { ...item }
          )
        );
        const resource = resources.find(
          (item) => item.id === entity?.titleResId!
        )!;
        if (!resource) return;
        resource.value = value as string;

        UpdateResourceAsync(state.gameDocument!, resource?.id, resource).then(
          (updatedGameDocument) => {
            setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          }
        );
      } else {
        setEntity((prev) => ({
          ...prev,
          [key as keyof AreaEntity]: value as string
        }));
        setState((prev) => {
          const resp = UpdateAreaAsync(
            prev.gameDocument!,
            areaId!,
            {
              ...entity,
              [key as keyof AreaEntity]: value as string
            },
            false
          );
          return UpdateGameDocState(prev, resp);
        });
      }
    } else {
      const copyEntity: AreaEntity = cloneDeep({ ...entity });
      if (copyEntity.events === undefined) {
        copyEntity.events = {
          onEntry: {} as Algorithm,
          onExit: {} as Algorithm
        };
      }
      copyEntity.events[key as keyof AreaEvent] = value as Algorithm;
      setState((prev) => {
        const resp = UpdateAreaAsync(
          prev.gameDocument!,
          areaId!,
          copyEntity,
          false
        );
        return UpdateGameDocState(prev, resp);
      });

      setEntity((prevEntity) => ({ ...prevEntity, ...copyEntity }));
    }
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteAreaAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        navigate('../areas');
      }
    );
  };

  const onViewEntity = (entityId: string) => {
    navigate(`../../map?areaId=${entityId}`);
  };

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const getAreaName = (areaId: string | undefined) => {
    if (areaId && state.gameDocument) {
      const area = GetAreaById(state.gameDocument, areaId);
      if (area) {
        return GetResourceValue(state.gameDocument!, area.titleResId ?? '');
      }
    }
  };

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/areas/*', text: 'Areas' },
      {
        path: '/designer/:gameId/assets/areas/:areaId/*',
        text: getAreaName(areaId)
      }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname, state.gameDocument]);

  return (
    <>
      <Toolbar
        title={GetResourceValue(state.gameDocument!, entity.titleResId ?? '')}
      />
      <ActionTopRow
        onViewClick={() => {
          onViewEntity(entity.id!);
        }}
        onDeleteClick={setShowConfirmationDelete}
      />
      <FluidForm>
        <AreaEditor
          editorMode={'full'}
          entity={entity}
          resources={resources}
          handleAreaChange={handleAreaChange}
          requiredFields={requiredFields}
        />
      </FluidForm>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(entity.id)
              : setShowConfirmationDelete()
          }
          onClose={() => setShowConfirmationDelete()}>
          Are you sure you want to remove the area?
        </YesNoDialog>
      )}
    </>
  );
};

export default Area;
